/**
 * Friends and friend requests via TanStack Query (docs/APP_PLAN.md §5.2,
 * §12 M4; APP_SPEC §14), following the same pattern as `lib/auth.ts`.
 * This is the only place `apps/web` talks to `/api/friends/*`.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';

import type { Friend, FriendRequestEntry } from '@perplexicon/api-client';

import { apiClient } from './api-client.js';

export const FRIENDS_QUERY_KEY = ['friends', 'list'] as const;
export const FRIENDS_INCOMING_QUERY_KEY = ['friends', 'incoming'] as const;
export const FRIENDS_OUTGOING_QUERY_KEY = ['friends', 'outgoing'] as const;

export function useFriends(): UseQueryResult<Friend[]> {
  return useQuery({ queryKey: FRIENDS_QUERY_KEY, queryFn: () => apiClient.friends.list() });
}

export function useIncomingFriendRequests(): UseQueryResult<FriendRequestEntry[]> {
  return useQuery({
    queryKey: FRIENDS_INCOMING_QUERY_KEY,
    queryFn: () => apiClient.friends.listIncoming(),
  });
}

export function useOutgoingFriendRequests(): UseQueryResult<FriendRequestEntry[]> {
  return useQuery({
    queryKey: FRIENDS_OUTGOING_QUERY_KEY,
    queryFn: () => apiClient.friends.listOutgoing(),
  });
}

export function useSendFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (targetUsername: string) => apiClient.friends.send(targetUsername),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FRIENDS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
    },
  });
}

export function useAcceptFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => apiClient.friends.accept(requestId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FRIENDS_INCOMING_QUERY_KEY });
      // Accepting a crossed request also resolves the acceptor's own
      // reciprocal *outgoing* request server-side (see the friends module's
      // `resolvePendingRequestsBetween`), so refresh that list too.
      void queryClient.invalidateQueries({ queryKey: FRIENDS_OUTGOING_QUERY_KEY });
      void queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY });
    },
  });
}

export function useDeclineFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => apiClient.friends.decline(requestId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: FRIENDS_INCOMING_QUERY_KEY }),
  });
}

export function useCancelFriendRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => apiClient.friends.cancel(requestId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: FRIENDS_OUTGOING_QUERY_KEY }),
  });
}

export function useRemoveFriend() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => apiClient.friends.remove(userId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: FRIENDS_QUERY_KEY }),
  });
}
